// components/trending/TrendingAnalytics.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle, TrendingUp, Activity, DollarSign } from 'lucide-react';

const formatNumber = (value, digits = 0) => {
    if (!Number.isFinite(value)) return '—';
    if (Math.abs(value) >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(digits)}M`;
    }
    if (Math.abs(value) >= 1_000) {
        return `${(value / 1_000).toFixed(digits)}K`;
    }
    return value.toFixed(digits);
};

const TrendingAnalytics = ({ onGameClick }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchGrowthLeaders = async () => {
            try {
                const response = await fetch('/analytics/v2/trends/growth/leaders?limit=50');
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const data = await response.json();
                if (isMounted) {
                    setPayload(data);
                }
            } catch (err) {
                console.error('[Trending] Failed to load growth leaders', err);
                if (isMounted) {
                    setError('Unable to load trending metrics right now.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchGrowthLeaders();
        return () => {
            isMounted = false;
        };
    }, []);

    const summary = useMemo(() => {
        if (!payload || !payload.snapshot) {
            return {
                topWishlist7d: null,
                topWishlist30d: null,
                topRevenue30d: null,
                totalGames: 0,
                window: null
            };
        }

        const leaders = payload.leaders || {};
        return {
            topWishlist7d: leaders.wishlist7d?.[0] || null,
            topWishlist30d: leaders.wishlist30d?.[0] || null,
            topRevenue30d: leaders.revenue30d?.[0] || null,
            totalGames: payload.snapshot.trackedGames || 0,
            window: payload.snapshot.analysisWindowDays || null
        };
    }, [payload]);

    const wishlistChartData = useMemo(() => {
        const leaders = payload?.leaders?.wishlist30d || [];
        return leaders.slice(0, 10).map(item => ({
            name: item.name,
            delta: item.delta
        }));
    }, [payload]);

    const entries = payload?.entries || [];

    const handleGameClick = (game) => {
        if (onGameClick && game?.steamId) {
            onGameClick(game);
        }
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <p className="text-gray-600">Loading trending metrics…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8">
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-6 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 space-y-8 h-full overflow-auto">
            <header className="space-y-2">
                <h2 className="text-2xl font-bold">Trending Growth</h2>
                <p className="text-gray-600">
                    Tracking wishlist, follower, and revenue momentum across {summary.totalGames.toLocaleString()} titles
                    {summary.window ? ` (last ${summary.window} days)` : ''}.
                </p>
            </header>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                    icon={<TrendingUp className="h-6 w-6" />}
                    title="Top Wishlist Surge (7d)"
                    value={summary.topWishlist7d ? summary.topWishlist7d.name : '—'}
                    footer={summary.topWishlist7d ? `+${formatNumber(summary.topWishlist7d.delta)} wishlists` : 'Awaiting data'}
                    color="green"
                />
                <MetricCard
                    icon={<Activity className="h-6 w-6" />}
                    title="Top Wishlist Surge (30d)"
                    value={summary.topWishlist30d ? summary.topWishlist30d.name : '—'}
                    footer={summary.topWishlist30d ? `+${formatNumber(summary.topWishlist30d.delta)} wishlists` : 'Awaiting data'}
                    color="blue"
                />
                <MetricCard
                    icon={<DollarSign className="h-6 w-6" />}
                    title="Revenue Momentum (30d)"
                    value={summary.topRevenue30d ? summary.topRevenue30d.name : '—'}
                    footer={summary.topRevenue30d ? `+$${formatNumber(summary.topRevenue30d.delta, 2)}` : 'Awaiting data'}
                    color="purple"
                />
            </section>

            <section className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Top 30-day Wishlist Growth</h3>
                    <span className="text-sm text-gray-500">Top {wishlistChartData.length} titles</span>
                </div>
                {wishlistChartData.length === 0 ? (
                    <EmptyState message="No growth data available yet." />
                ) : (
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={wishlistChartData} layout="vertical" margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={value => formatNumber(value)} />
                                <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(value) => formatNumber(value)} />
                                <Legend />
                                <Bar dataKey="delta" fill="#3B82F6" name="Wishlist Δ" barSize={18} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </section>

            <section className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Leaders (Top 20)</h3>
                    <span className="text-sm text-gray-500">Sorted by 30-day wishlist delta</span>
                </div>
                {entries.length === 0 ? (
                    <EmptyState message="No tracked games yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-left">
                                <tr>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Game</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Publisher Class</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Wishlists</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Δ 7d</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Δ 30d</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Followers Δ 30d</th>
                                    <th className="px-4 py-2 font-semibold text-gray-600">Revenue Δ 30d</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.slice(0, 20).map(entry => (
                                    <tr key={entry.steamId} className="border-t border-gray-100">
                                        <td className="px-4 py-2 text-gray-900 font-medium">
                                            <button
                                                className="text-left hover:text-blue-600 font-semibold"
                                                onClick={() => handleGameClick(entry)}
                                            >
                                                {entry.name}
                                            </button>
                                        </td>
                                        <td className="px-4 py-2 text-gray-600">{entry.publisherClassDisplay || entry.publisherClass || 'Hobbyist'}</td>
                                        <td className="px-4 py-2 text-gray-900">{formatNumber(entry.latest.wishlists)}</td>
                                        <td className="px-4 py-2 text-green-600">{entry.deltas.wishlist7d ? `+${formatNumber(entry.deltas.wishlist7d)}` : '—'}</td>
                                        <td className="px-4 py-2 text-green-600">{entry.deltas.wishlist30d ? `+${formatNumber(entry.deltas.wishlist30d)}` : '—'}</td>
                                        <td className="px-4 py-2 text-blue-600">{entry.deltas.follower30d ? `+${formatNumber(entry.deltas.follower30d)}` : '—'}</td>
                                        <td className="px-4 py-2 text-purple-600">{entry.deltas.revenue30d ? `+$${formatNumber(entry.deltas.revenue30d, 2)}` : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-6 flex items-center gap-3 text-gray-500">
        <AlertCircle className="w-5 h-5" />
        <span>{message}</span>
    </div>
);

const MetricCard = ({ icon, title, value, footer, color = 'blue' }) => {
    const colors = {
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600'
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
                <div className={`p-3 rounded-full ${colors[color] || colors.blue}`}>
                    {icon}
                </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mt-4">{title}</h3>
            <p className="text-xl font-semibold text-gray-900 mt-2 truncate">{value}</p>
            <p className="text-sm text-gray-500 mt-1">{footer}</p>
        </div>
    );
};

export default TrendingAnalytics;
